import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { AnalyticsHooks, DemoDataBanner, GeneralFeedbackButton } from '@frontend/analytics-shared';
import { Trans, useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { BreadcrumbPathProps, Text } from '@frontend/design-system';
import { URLs } from '../../../../constants';
import { featureFlags } from '../../../../feature-flags';
import { DidYouKnow } from '../../components/did-you-know';
import { customROITimePeriods } from '../../constants';
import { getDateRangeText } from '../../utils/date-range-text';
import { ARCharts } from '../components/ar-charts';
import { ARRoiMultiSubView } from '../components/ar-roi-multi-sub-view';
import { ARRoiSingleSubView } from '../components/ar-roi-single-sub-view';
import { ARRoiTable } from '../components/ar-roi-table';
import { ARSummary } from '../components/ar-summary';
import { Filters } from '../components/filters';
import { HighlightARValue } from '../components/highlight-ar-value';
import { useARRoiShallowStore } from '../hooks/use-ar-roi-store';
import { useFetchARROIOOverview } from '../hooks/use-fetch-ar-roi-overview';

export const ARRoiDashboard = () => {
  const { t } = useTranslation('analytics');
  const {
    filterHintText: subtitle,
    filters,
    setDefinitionsHelperDateRange,
  } = useARRoiShallowStore('filterHintText', 'filters', 'setDefinitionsHelperDateRange');
  const { averageProductionAmount } = AnalyticsHooks.useAverageProductionAmount();

  const search = useSearch({ strict: false }) ?? {
    view: 'arConversion',
  };
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.appointmentRemindersRoiDemoData);

  const subViewTitle = useMemo(() => {
    let title = '';

    switch (search.view) {
      case 'arConversion':
        title = t('Appointment Reminder Conversion');
        break;

      case 'noShowRate':
        title = t('No Show Rate');
        break;
    }

    return title;
  }, [search.view]);

  const breadcrumbs: BreadcrumbPathProps[] = useMemo(() => {
    const values: BreadcrumbPathProps[] = [
      {
        label: t('Analytics'),
        to: URLs.ANALYTICS_DASHBOARD,
      },
      {
        label: t('Appointment Reminder'),
        to: URLs.ROI_APPOINTMENT_REMINDERS,
      },
    ];

    if (subViewTitle) {
      values.push({
        label: subViewTitle,
      });
    }

    return values;
  }, [subViewTitle]);

  const fetchOverviewProps = useFetchARROIOOverview({
    filters,
  });
  const { aggregated, isLoading } = fetchOverviewProps;

  useEffect(() => {
    setDefinitionsHelperDateRange(
      getDateRangeText({
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        timePeriods: customROITimePeriods,
      })
    );
  }, [filters?.startDate, filters?.endDate]);

  return (
    <>
      {isDemoAccount && (
        <DemoDataBanner title={t("You're taking Appointment Reminders Conversion for a test drive with demo data!")} />
      )}
      <Page>
        <Page.Header>
          <Page.Header.Breadcrumbs breadcrumbs={breadcrumbs} />
          <Page.Header.Heading>
            <Page.Header.Title title={subViewTitle || t('Appointment Reminder Conversion')} />
            <Page.Header.Subtitle subtitle={t('Showing results for {{subtitle}}', { subtitle })} />
            <Page.Header.Action>
              <GeneralFeedbackButton selectedLocationFromPageFilter={filters?.locationIds} />
            </Page.Header.Action>
          </Page.Header.Heading>
        </Page.Header>

        <Page.Body css={styles.body}>
          <div css={styles.body}>
            <Filters isLoading={isLoading} />

            {subViewTitle ? (
              <>
                {(filters?.locationIds?.length || 0) > 1 ? (
                  <ARRoiMultiSubView {...fetchOverviewProps} />
                ) : (
                  <ARRoiSingleSubView {...fetchOverviewProps} />
                )}
                <ARRoiTable trackingIdBase='ar-roi-patients-table' />
              </>
            ) : (
              <>
                <HighlightARValue
                  value={
                    aggregated.appointmentsCompleted.appointmentsCompletedByReminders * averageProductionAmount.value
                  }
                />
                <ARSummary data={aggregated} isLoading={isLoading} />
                <ARCharts {...fetchOverviewProps} />
                <DidYouKnow>
                  <Text size='medium'>
                    <Trans t={t}>
                      Practices that use appointment reminders have <strong>10%</strong> lower no show rate compared to
                      practices that do not use appointment reminders.
                    </Trans>
                  </Text>
                </DidYouKnow>
              </>
            )}
          </div>
        </Page.Body>
      </Page>
    </>
  );
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
};
